import { accountTypeKeyToPlanTypeId, planTypeIdToDashboardCategory } from '../../../../constants'

// Accounts that never appear in the investments step: brokerage and other-asset
// accounts. GuidedChoice doesn't provide investment advice for these, so they're
// excluded regardless of their advised flag.
const EXCLUDED_PLAN_TYPES = new Set([
  accountTypeKeyToPlanTypeId['brokerage-account'],
  accountTypeKeyToPlanTypeId['other-asset'],
])

// IRAs map to category 'other', so they never count as an employer plan here.
const isContributableEmployerPlan = account =>
  account.contributionEligibility === true &&
  planTypeIdToDashboardCategory[account.planType] === 'employer'

/**
 * Tab ordering rank for the investments step (lower shows first):
 *   0. institutional account
 *   1. participant's contributable employer plans
 *   2. spouse's contributable employer plans
 *   3. all other accounts
 * Within a rank, ties break by balance descending, then name.
 */
const tabRank = ({ account, personId, spouseId }) => {
  if (account.type === 'Institutional') return 0
  if (isContributableEmployerPlan(account)) {
    if (account.participantId === personId) return 1
    if (spouseId != null && account.participantId === spouseId) return 2
  }
  return 3
}

// Rank for a breakdown account we can't match to any account metadata by name —
// it still shows (the engine returned an advice breakdown for it), just after
// every metadata-ranked account.
const UNMATCHED_RANK = 4

/**
 * The ordered, filtered list of accounts shown as tabs in the investments step.
 *
 * The engine's `breakdown/all` is the source of truth for which accounts have
 * advice to show (the existing overall-strategy account dropdown is driven the
 * same way), so the tab list is derived from it. Account metadata (from
 * `store.accounts`, correlated by name) is used only to drop brokerage /
 * other-asset accounts and to order the tabs; a breakdown account with no
 * metadata match still shows, ranked last.
 *
 * @param sortedAccounts store.sortedAccounts (metadata: type, planType,
 *                       eligibility, participant, balance — used for ordering/exclusion)
 * @param allBreakdown   store.allBreakdown / allBreakdownDetailed
 * @param personId       store.person.id
 * @param spouseId       store.spouse?.id
 * @returns {Array<{ id: string, name: string, isInstitutional: boolean }>}
 */
export const buildInvestmentTabs = ({
  sortedAccounts = [],
  allBreakdown = [],
  personId,
  spouseId,
} = {}) => {
  const metaByName = new Map(sortedAccounts.map(account => [account.name, account]))

  return (
    allBreakdown
      .map(breakdown => ({ name: breakdown.name, account: metaByName.get(breakdown.name) }))
      // Exclude brokerage / other-asset accounts when the metadata identifies them.
      .filter(({ account }) => !account || !EXCLUDED_PLAN_TYPES.has(account.planType))
      .map(({ name, account }) => ({
        id: name,
        name,
        isInstitutional: account?.type === 'Institutional',
        rank: account ? tabRank({ account, personId, spouseId }) : UNMATCHED_RANK,
        balance: account?.balance ?? 0,
      }))
      .sort((a, b) => a.rank - b.rank || b.balance - a.balance || a.name.localeCompare(b.name))
      .map(({ rank, balance, ...tab }) => tab)
  )
}

/**
 * Whether the investments step is reachable: true when at least one account
 * qualifies. Advice uses this to skip/guard the step (mirrors hasIncludedGoals).
 */
export const hasInvestmentAccounts = args => buildInvestmentTabs(args).length > 0
